import { Client } from '@hapi/nes/lib/client';
import type { DirectoryUser } from '@poweralpha/common';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, getAuthHeader, getBearerToken } from '../api';
import { useAuth } from '../auth';
import {
  activeSchema,
  archiveSchema,
  CONTRACT_ID,
  feedbackResponseSchema,
  listSchema,
  type ServerMessage,
  type ServerWorkspace,
  serverMessageSchema,
  singleTurnSchema,
  snapshotSchema,
  workspaceConflictSchema,
  workspaceSchema,
} from './session/contract';
import type {
  AgentArtifact,
  AgentConversationShare,
  AgentMessage,
  AgentTurnFeedback,
  AgentTurnFeedbackPayload,
  UploadedFileMeta,
} from './types';
import type { toWorkspacePayload } from './workspace/agentWorkspaceStore';

interface OpenAgentSocketOptions {
  onEvent: (event: ServerMessage) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Error) => void;
}

export interface AgentSocket {
  readonly readyState: number;
  close: () => void;
  send: (payload: string) => Promise<unknown>;
}

export interface AgentMessagePdfDownload {
  blob: Blob;
  filename: string;
}

export const AGENT_CONVERSATION_PAGE_SIZE = 20;

export const AGENT_CONVERSATION_TURN_PAGE_SIZE = 20;

export const agentConversationListQueryKey = ['agent-conversations'] as const;

export const agentConversationSearchQueryKey = ['agent-conversations-search'] as const;

export const agentConversationSharesQueryKey = (id: string | undefined, user: string | undefined) =>
  ['agent-conversation-shares', id, user] as const;

const userHeader = (user: string | undefined) => ({
  'X-Agent-Contract': CONTRACT_ID,
  ...(user ? { user } : {}),
});

export const listAgentConversations = (
  user: string | undefined,
  { limit = 20, offset = 0, search = '' } = {},
) =>
  api
    .get('proxy/agent/conversations', {
      headers: userHeader(user),
      searchParams: { limit, offset, q: search },
    })
    .json<unknown>()
    .then((value) => listSchema.parse(value));

export const listActiveAgentConversations = (user: string | undefined) =>
  api
    .get('proxy/agent/conversations/active', { headers: userHeader(user) })
    .json<unknown>()
    .then((value) => activeSchema.parse(value));

export const getAgentConversation = (
  id: string,
  user: string | undefined,
  { limit = 20, beforeTurnIndex }: { limit?: number; beforeTurnIndex?: number } = {},
) =>
  api
    .get(`proxy/agent/conversations/${encodeURIComponent(id)}`, {
      headers: userHeader(user),
      searchParams: {
        limit,
        ...(beforeTurnIndex !== undefined ? { before_turn_index: beforeTurnIndex } : {}),
      },
    })
    .json<unknown>()
    .then((value) => snapshotSchema.parse(value));

export const getAgentTurn = (id: string, turnId: string, user: string | undefined) =>
  api
    .get(
      'proxy/agent/conversations/' +
        encodeURIComponent(id) +
        '/turns/' +
        encodeURIComponent(turnId),
      { headers: userHeader(user) },
    )
    .json<unknown>()
    .then((value) => singleTurnSchema.parse(value));

export const archiveAgentConversation = (id: string, user: string | undefined) =>
  api
    .post(`proxy/agent/conversations/${encodeURIComponent(id)}/archive`, {
      headers: userHeader(user),
    })
    .json<unknown>()
    .then((value) => archiveSchema.parse(value));

const workspacePath = (id: string) =>
  `proxy/agent/conversations/${encodeURIComponent(id)}/workspace`;

export const getAgentWorkspace = (id: string, user: string | undefined) =>
  api
    .get(workspacePath(id), { headers: userHeader(user) })
    .json<unknown>()
    .then((value) => workspaceSchema.parse(value));

export type AgentWorkspaceSaveResult =
  | { status: 'saved'; workspace: ServerWorkspace }
  | { status: 'conflict'; workspace: ServerWorkspace };

/** Saves the caller's workspace (POST: the WAF blocks PUT). A stale revision is a conflict. */
export const saveAgentWorkspace = async (
  id: string,
  user: string | undefined,
  payload: ReturnType<typeof toWorkspacePayload>,
): Promise<AgentWorkspaceSaveResult> => {
  const response = await api.post(workspacePath(id), {
    headers: userHeader(user),
    json: payload,
    throwHttpErrors: false,
  });
  if (response.status === 409) {
    const conflict = workspaceConflictSchema.parse(await response.json());
    return { status: 'conflict', workspace: conflict.workspace };
  }
  if (!response.ok) throw new Error(`Saving the workspace failed (${response.status})`);
  return { status: 'saved', workspace: workspaceSchema.parse(await response.json()) };
};

export const useAgentConversations = (user: string | undefined) =>
  useInfiniteQuery({
    queryKey: [...agentConversationListQueryKey, user],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => listAgentConversations(user, { offset: pageParam }),
    getNextPageParam: (page) => page.next_offset ?? undefined,
    enabled: Boolean(user),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useAgentConversationSearch = (user: string | undefined, search: string) =>
  useInfiniteQuery({
    queryKey: [...agentConversationSearchQueryKey, user, search],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => listAgentConversations(user, { offset: pageParam, search }),
    getNextPageParam: (page) => page.next_offset ?? undefined,
    enabled: Boolean(user && search.trim()),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const listConversationShares = (
  conversationId: string,
  user: string | undefined,
): Promise<Array<AgentConversationShare>> =>
  api
    .get(`proxy/agent/conversations/${conversationId}/shares`, { headers: userHeader(user) })
    .json<Array<AgentConversationShare>>();

export const addConversationShare = (
  conversationId: string,
  grantee: string,
  user: string | undefined,
): Promise<AgentConversationShare> =>
  api
    .post(`proxy/agent/conversations/${conversationId}/shares`, {
      json: { grantee },
      headers: userHeader(user),
    })
    .json<AgentConversationShare>();

// POST (not DELETE): the agent proxy only registers GET/POST/PUT wildcard routes,
// so a DELETE would 404 before reaching the Python backend.
export const removeConversationShare = (
  conversationId: string,
  grantee: string,
  user: string | undefined,
): Promise<void> =>
  api
    .post(
      `proxy/agent/conversations/${conversationId}/shares/${encodeURIComponent(grantee)}/remove`,
      {
        headers: userHeader(user),
      },
    )
    .then(() => undefined);

/**
 * Searches the Entra directory (server-side Graph proxy) for people to share
 * with. `activeOnly` (default true) restricts results to enabled accounts.
 */
export const searchDirectoryUsers = (
  query: string,
  activeOnly = true,
): Promise<Array<DirectoryUser>> =>
  api
    .get('directory/users', { searchParams: { q: query, activeOnly } })
    .json<Array<DirectoryUser>>();

export const useConversationShares = (
  conversationId: string | undefined,
  user: string | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: agentConversationSharesQueryKey(conversationId, user),
    queryFn: () => listConversationShares(conversationId ?? '', user),
    enabled: enabled && Boolean(conversationId && user),
    retry: false,
  });

export function useAgentArtifactJson<T>(artifact: AgentArtifact | undefined) {
  const { user } = useAuth();
  const nickname = user?.nickname;

  return useQuery<T>({
    queryKey: ['agent-artifact-json', artifact?.id, artifact?.downloadUrl, nickname],
    enabled: Boolean(artifact?.downloadUrl),
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      if (!artifact?.downloadUrl) throw new Error('Missing artifact download URL');

      const headers: Record<string, string> = {
        authorization: await getAuthHeader(),
        'X-Agent-Contract': CONTRACT_ID,
      };

      if (nickname) headers.user = nickname;

      const response = await fetch(artifact.downloadUrl, { headers });

      if (!response.ok) throw new Error(`Artifact fetch failed (${response.status})`);

      return response.json() as Promise<T>;
    },
  });
}

export const uploadAgentDocument = (
  file: File,
  conversationId: string,
  user: string | undefined,
): Promise<UploadedFileMeta> => {
  const body = new FormData();

  body.append('file', file, file.name);

  return api
    .post(`proxy/agent/uploads/${conversationId}`, {
      body,
      headers: userHeader(user),
      timeout: 180e3,
    })
    .json<UploadedFileMeta>();
};

/** Generates one assistant-message PDF only after the user invokes the export action. */
export const exportAgentMessagePdf = async (
  content: string,
  plots: NonNullable<AgentMessage['plots']>,
  conversationId: string,
  user: string | undefined,
): Promise<AgentMessagePdfDownload> => {
  const response = await api.post(
    `proxy/agent/conversations/${conversationId}/exports/message-pdf`,
    {
      json: { content, plots, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      headers: userHeader(user),
      timeout: 180e3,
    },
  );
  const mimeType = (response.headers.get('content-type') ?? '').split(';')[0].trim().toLowerCase();

  if (mimeType !== 'application/pdf') {
    throw new Error('PDF export returned an unexpected response. Please retry.');
  }

  return {
    blob: await response.blob(),
    filename: responseFilename(response.headers.get('content-disposition')),
  };
};

/**
 * Submits, changes or clears the rating on one answer. Everything is a POST: the
 * WAF blocks PUT and the proxy never routed DELETE, so clearing is `rating: 0`.
 */
export const submitAgentTurnFeedback = (
  conversationId: string,
  turnId: string,
  payload: AgentTurnFeedbackPayload,
  user: string | undefined,
): Promise<AgentTurnFeedback | null> =>
  api
    .post(`proxy/agent/conversations/${conversationId}/turns/${turnId}/feedback`, {
      json: payload,
      headers: userHeader(user),
    })
    .json<unknown>()
    .then((value) => feedbackResponseSchema.parse(value).feedback);

export const getAgentChatWebSocketUrl = () => {
  const explicitHost = import.meta.env.VITE_AGENT_WS_HOST;
  const baseUrl = explicitHost
    ? `${explicitHost.replace(/\/$/, '')}/api/v1/proxy/agent/chat`
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
        window.location.host
      }/api/v1/proxy/agent/chat`;

  return new URL(baseUrl).toString();
};

export const openAgentSocket = async ({
  onEvent,
  onOpen,
  onClose,
  onError,
}: OpenAgentSocketOptions): Promise<AgentSocket> => {
  const token = await getBearerToken();
  const client = new Client(getAgentChatWebSocketUrl() as `ws://${string}` | `wss://${string}`);
  const socket = new NesAgentSocket(client, onError);

  client.onConnect = () => {
    socket.markOpen();
    onOpen?.();
  };

  client.onUpdate = (message) => {
    try {
      const raw = typeof message === 'string' ? message : JSON.stringify(message);

      onEvent(serverMessageSchema.parse(JSON.parse(raw)));
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Failed to parse agent event'));
    }
  };

  client.onError = (error) => {
    onError?.(error);
  };

  client.onDisconnect = (_willReconnect, log) => {
    socket.markClosed();

    onClose?.({
      code: log.code,
      reason: log.reason,
      wasClean: log.wasClean,
    } as CloseEvent);
  };

  void client
    .connect({
      auth: { headers: { authorization: `Bearer ${token}` } },
      reconnect: false,
      timeout: 10_000,
    })
    .catch((error) => {
      onError?.(error instanceof Error ? error : new Error('Agent socket connection failed'));
      if (socket.readyState === 3) return;
      socket.markClosed();
      onClose?.({ code: 1006, reason: 'Connection failed', wasClean: false } as CloseEvent);
    });

  return socket;
};

const responseFilename = (contentDisposition: string | null): string => {
  // Prefer the RFC 5987 encoded form (filename*=charset'lang'...) over the plain one.
  const encoded = contentDisposition?.match(/filename\*=(?:[\w-]+'[^']*')?([^;]+)/i)?.[1]?.trim();

  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall through to the plain filename below.
    }
  }

  const filename = contentDisposition?.match(/filename="?([^";*]+)"?/i)?.[1]?.trim();

  return filename || 'engineer-agent-response.pdf';
};

class NesAgentSocket implements AgentSocket {
  readyState = 0;

  constructor(
    private readonly client: Client,
    private readonly onError?: (error: Error) => void,
  ) {}

  markOpen() {
    this.readyState = 1;
  }

  markClosed() {
    this.readyState = 3;
  }

  send(payload: string) {
    if (this.readyState !== 1) {
      throw new Error('Agent socket is not open');
    }

    const message = JSON.parse(payload) as unknown;

    return this.client.message(message).catch((error) => {
      this.onError?.(
        error instanceof Error ? error : new Error('Failed to send agent socket message'),
      );

      throw error;
    });
  }

  close() {
    if (this.readyState >= 2) return;
    this.readyState = 2;

    void this.client.disconnect().finally(() => {
      this.markClosed();
    });
  }
}
